import React, { useState } from 'react';

import { ContextMenu, MENU_HORIZONTAL_POSITIONS, MENU_VERTICAL_POSITIONS } from 'lib/context-menu';
import { rotateHalfwayAnimation } from 'lib/core';
import { IconArrowDropDown } from 'lib/icons';
import { KEY_VALUES } from 'lib/utilities';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { DropdownOption } from './DropdownOption';
import { OutlineKeyboard } from './OutlineKeyboard';
import { getRem } from '../../../core';
import { PageHeaderTitle, PageHeaderWrapper } from '../../elements';

const StyledContextMenu = styled(ContextMenu)`
  left: 0;
  margin-left: ${({ theme }) => theme.size.spacing.large.value};
  margin-top: ${getRem('20px')};
  overflow-y: auto;
  transform: none;
  width: calc(100% - ${getRem('32px')});
`;

const StyledIconArrowDropDown = styled(IconArrowDropDown).withConfig({
  shouldForwardProp: (prop) => !['isOpen'].includes(prop),
})`
  align-self: center;
  animation: ${rotateHalfwayAnimation} 0.15s linear reverse;
  fill: currentColor;
  ${({ isOpen }) =>
    isOpen &&
    css`
      animation: ${rotateHalfwayAnimation} 0.15s linear;
      transform: rotate(180deg);
    `};
`;

const StyledHeaderContainer = styled.span`
  color: ${({ theme }) => theme.color.additional.dark.value};
  display: inline-flex;
  margin: ${getRem('12px')} ${({ theme }) => theme.size.spacing.large.value};
  margin-left: ${({ theme }) => theme.size.spacing.medium.value};
  overflow: hidden;
  padding-left: ${({ theme }) => theme.size.spacing.small.value};
  position: relative;

  &:hover {
    cursor: pointer;
  }
`;

const StyledPageHeaderTitle = styled(PageHeaderTitle)`
  padding: 0;
  padding-left: ${({ theme }) => theme.size.spacing.small.value};
`;

const PageHeaderTitleWithDropdown = ({ dataTestId = '', onSelect, options, selectedOption, ...other }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleClose = () => setIsOpen(false);
  const handleOpen = () => setIsOpen(true);
  const handleSelect = (option) => onSelect(option, setIsOpen);

  const toggleSetIsOpen = () => setIsOpen(!isOpen);
  const StyledPageHeaderContentWrapper = styled.span.attrs(() => ({
    'data-testid': `${dataTestId}-with-dropdown`,
  }))``;

  const wrappedComponentContent = (
    <OutlineKeyboard
      as={StyledPageHeaderContentWrapper}
      innerProps={{ as: StyledHeaderContainer }}
      onClick={toggleSetIsOpen}
      onKeyPress={toggleSetIsOpen}
      role="button"
      tabIndex={0}
    >
      <StyledPageHeaderTitle data-testid={dataTestId ? `${dataTestId}-display-text` : undefined}>
        {selectedOption.label}
      </StyledPageHeaderTitle>
      <StyledIconArrowDropDown data-testid={dataTestId ? `${dataTestId}-arrow-icon` : undefined} isOpen={isOpen} />
    </OutlineKeyboard>
  );

  return (
    <PageHeaderWrapper data-testid={dataTestId} {...other}>
      <StyledContextMenu
        customRenderOption={(option, ref, index, handleClick) => {
          const handleKeyPress = (event) => {
            const keyValue = event.key || undefined;
            if (keyValue === KEY_VALUES.ENTER || keyValue === KEY_VALUES.SPACE) {
              handleClick();
            }
          };
          return (
            <DropdownOption
              id={option.id}
              data-testid={dataTestId ? `${dataTestId}-dropdown-option-${index}` : undefined}
              isFocused={!!(ref && ref.current)}
              isSelected={option.id === selectedOption.id}
              label={option.label}
              onClick={handleClick}
              onKeyPress={handleKeyPress}
              tabIndex={0}
              ref={ref}
            />
          );
        }}
        dataTestId={dataTestId ? `${dataTestId}-context-menu` : undefined}
        onClose={handleClose}
        onOpen={handleOpen}
        onSelect={handleSelect}
        positionHorizontal={MENU_HORIZONTAL_POSITIONS.CENTER}
        positionVertical={MENU_VERTICAL_POSITIONS.CENTER}
        {...{ isOpen, options, wrappedComponentContent }}
      />
    </PageHeaderWrapper>
  );
};

PageHeaderTitleWithDropdown.propTypes = {
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** Callback called on option click or keypress, forwards selected option, setIsOpen callback  */
  onSelect: PropTypes.func.isRequired,
  /** List of options to select from */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
    }).isRequired
  ).isRequired,
  /** Current selected option */
  selectedOption: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
  }).isRequired,
};

export { PageHeaderTitleWithDropdown };
